import { PDFLayoutOptions, CompanyInfo } from './pdfService';

export interface PDFTemplateSettings extends PDFLayoutOptions {
  companyInfo: CompanyInfo;
  id?: string;
  name: string;
  description: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DefaultPDFSettings {
  template: PDFTemplateSettings;
  lastUpdated: string;
}

const STORAGE_KEY = 'cnf_pdf_template_settings_v2'; // Added version to avoid conflicts
const DEFAULT_COMPANY_INFO: CompanyInfo = {
  name: 'CNF Auto LLC',
  address: '123 Main Street',
  city: 'City',
  state: 'State',
  zip: '12345',
  phone: '(555) 123-4567',
  email: 'info@cnfautosuite.com',
  website: 'www.cnfautosuite.com',
};

export class PDFTemplateService {
  private static readonly DEFAULT_TEMPLATE: PDFTemplateSettings = {
    id: 'default',
    name: 'Default Template',
    description: 'Standard PDF template with professional layout',
    isDefault: true,
    layout: 'professional',
    includeCompanyLogo: true,
    showInternalNotes: false,
    showPartDetails: true,
    showDiscountBreakdown: true,
    highlightDiscounts: true,
    showOriginalPrices: true,
    colorScheme: 'blue',
    fontFamily: 'helvetica',
    fontSize: 'medium',
    spacing: 'normal',
    borderStyle: 'simple',
    showPageNumbers: true,
    companyInfo: DEFAULT_COMPANY_INFO,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  /**
   * Get the current default PDF template settings
   */
  static getDefaultSettings(): PDFTemplateSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: DefaultPDFSettings = JSON.parse(stored);
        return parsed.template;
      }
    } catch (error) {
      console.warn('Failed to load PDF template settings from localStorage:', error);
    }

    return { ...this.DEFAULT_TEMPLATE };
  }

  /**
   * Save default PDF template settings
   */
  static saveDefaultSettings(settings: Partial<PDFTemplateSettings>): void {
    try {
      const currentSettings = this.getDefaultSettings();
      const updatedSettings: PDFTemplateSettings = {
        ...currentSettings,
        ...settings,
        updatedAt: new Date().toISOString(),
      };

      const toSave: DefaultPDFSettings = {
        template: updatedSettings,
        lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      console.log('PDF template settings saved successfully');
    } catch (error) {
      console.error('Failed to save PDF template settings to localStorage:', error);
      throw new Error('Failed to save PDF template settings');
    }
  }

  /**
   * Update company information in default settings
   */
  static updateCompanyInfo(companyInfo: Partial<CompanyInfo>): void {
    const currentSettings = this.getDefaultSettings();
    const updatedCompanyInfo = {
      ...currentSettings.companyInfo,
      ...companyInfo,
    };

    this.saveDefaultSettings({
      companyInfo: updatedCompanyInfo,
    });
  }

  /**
   * Update layout options in default settings
   */
  static updateLayoutOptions(options: Partial<PDFLayoutOptions>): void {
    const currentSettings = this.getDefaultSettings();
    this.saveDefaultSettings({
      ...options,
    });
  }

  /**
   * Reset to default settings
   */
  static resetToDefaults(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to reset PDF template settings:', error);
    }
  }

  /**
   * Get layout options only (excluding company info)
   */
  static getDefaultLayoutOptions(): PDFLayoutOptions {
    const settings = this.getDefaultSettings();
    const { companyInfo, id, name, description, isDefault, createdAt, updatedAt, ...layoutOptions } = settings;
    return layoutOptions;
  }

  /**
   * Get company info only
   */
  static getDefaultCompanyInfo(): CompanyInfo {
    const settings = this.getDefaultSettings();
    return settings.companyInfo;
  }

  /**
   * Check if settings have been customized from defaults
   */
  static hasCustomSettings(): boolean {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Test localStorage functionality
   */
  static testLocalStorage(): boolean {
    try {
      const testKey = 'cnf_test_key';
      const testValue = 'test_value';
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      return retrieved === testValue;
    } catch (error) {
      console.error('localStorage test failed:', error);
      return false;
    }
  }

  /**
   * Export settings as JSON
   */
  static exportSettings(): string {
    const settings = this.getDefaultSettings();
    return JSON.stringify(settings, null, 2);
  }

  /**
   * Import settings from JSON
   */
  static importSettings(jsonString: string): void {
    try {
      const imported = JSON.parse(jsonString);

      // Validate imported settings structure
      if (!imported || typeof imported !== 'object') {
        throw new Error('Invalid settings format');
      }

      // Merge with defaults to ensure all required fields exist
      const mergedSettings = {
        ...this.DEFAULT_TEMPLATE,
        ...imported,
        id: 'default',
        isDefault: true,
        updatedAt: new Date().toISOString(),
      };

      this.saveDefaultSettings(mergedSettings);
    } catch (error) {
      console.error('Failed to import PDF template settings:', error);
      throw new Error('Failed to import settings. Please check the file format.');
    }
  }

  /**
   * Validate company logo (base64 or URL)
   */
  static validateLogo(logo: string): boolean {
    if (!logo) return true; // Logo is optional

    // Check if it's a data URL (base64)
    if (logo.startsWith('data:image/')) {
      return true;
    }

    // Check if it's a valid URL
    try {
      new URL(logo);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get settings with overrides for one-time use
   */
  static getSettingsWithOverrides(overrides: Partial<PDFLayoutOptions> = {}): PDFLayoutOptions {
    const defaultOptions = this.getDefaultLayoutOptions();
    return {
      ...defaultOptions,
      ...overrides,
    };
  }

  /**
   * Get company info with overrides for one-time use
   */
  static getCompanyInfoWithOverrides(overrides: Partial<CompanyInfo> = {}): CompanyInfo {
    const defaultCompanyInfo = this.getDefaultCompanyInfo();
    return {
      ...defaultCompanyInfo,
      ...overrides,
    };
  }
}